import { BlockChange, WorkflowBlock } from '@basehub/data/replicache/index'
import { ArrowRightIcon } from '@radix-ui/react-icons'
import { clsx } from 'clsx'
import * as React from 'react'
import { useWorkingTreeStore } from '~/hooks-data/blocks-tree/provider'

import { ButtonLink } from '~/sotano-kit/primitives/button'
import { useDashboardSlugs } from '~/hooks/use-dashboard-slugs'
import isEqual from 'lodash.isequal'
import { makeShortNameFromClerkUser } from '@basehub/data/auth/utils/make-full-name-from-clerk-user'
import { useRepoMembers } from '~/hooks-data/use-repo-members'
import { PublicUserData } from '~/hooks-data/use-user-data'

type WorkflowBlockDiffProps = {
  condition: 'removed' | 'added'
  change: BlockChange
}

export const WorkflowBlockDiff = (props: WorkflowBlockDiffProps) => {
  const isDeletedAndConditionIsAdded =
    props.condition === 'added' && props.change.status === 'deleted'

  return (
    <div
      className={clsx(
        'text-xs space-y-3 text-dark-dim',
        isDeletedAndConditionIsAdded && 'opacity-30'
      )}
    >
      <div className="flex items-baseline gap-x-0.5">
        <span className="font-medium text-dark-muted">
          When:&nbsp;&nbsp;&nbsp;
        </span>
        <WhenDiff {...props} />
      </div>
      <div className="grid items-baseline gap-x-0.5 grid-cols-[auto_1fr] gap-y-2">
        <span className="font-medium text-dark-muted col-start-1 whitespace-nowrap">
          Do this:&nbsp;
        </span>
        <DoDiff {...props} />
      </div>
    </div>
  )
}

function WhenDiff({
  change,
  condition,
}: WorkflowBlockDiffProps): React.ReactNode {
  const { repoSlug, teamSlug, branchOrCommit } = useDashboardSlugs()
  const isConflictingOnTrigger =
    change.status === 'conflict' &&
    change.conflictPaths.includes('value.trigger')
  const beforeBlock = isConflictingOnTrigger
    ? change.conflicting
    : change.previous
  const isDeletedAndConditionIsAdded =
    condition === 'added' && change.status === 'deleted'
  const trigger = React.useMemo(() => {
    const workflowBlockValue = isDeletedAndConditionIsAdded
      ? ((change.conflicting?.value ??
          change.previous?.value) as WorkflowBlock['value'])
      : ((condition === 'removed'
          ? (change.conflicting?.value ?? change.previous?.value)
          : change.current?.value) as WorkflowBlock['value'])

    return workflowBlockValue?.trigger
  }, [change, condition, isDeletedAndConditionIsAdded])
  const getNode = useWorkingTreeStore((s) => s.tree?.getNode)

  const status = React.useMemo(() => {
    const beforeTrigger = (beforeBlock as WorkflowBlock | null)?.value.trigger
    const currentTrigger = (change.current as WorkflowBlock)?.value.trigger
    if (!beforeTrigger || !currentTrigger) return 'both-changed'
    if (beforeTrigger?.type !== currentTrigger?.type) return 'both-changed'
    if (
      beforeTrigger.type === 'list-block' &&
      currentTrigger.type === 'list-block' &&
      !isEqual(beforeTrigger.events, currentTrigger.events)
    ) {
      return 'event-changed'
    }
    if (
      'blockId' in beforeTrigger &&
      'blockId' in currentTrigger &&
      beforeTrigger.blockId !== currentTrigger.blockId
    ) {
      return 'block-changed'
    }
    return 'unmodified'
  }, [beforeBlock, change])

  const firstType = React.useMemo<React.ReactNode>(() => {
    if (!getNode) return null
    switch (trigger?.type) {
      case 'commit':
        return 'A commit happens'
      case 'list-block': {
        const block = getNode(trigger.blockId)?.value
        if (!block) return null
        return (
          <ButtonLink
            unstyled
            focusStyled
            className="hover:text-dark-bright transition-colors underline"
            href={`/${teamSlug}/${repoSlug}/explore/${branchOrCommit}/${block.id}`}
          >
            {block.title}
          </ButtonLink>
        )
      }
      case 'event-block': {
        const node = getNode(trigger.blockId)
        if (!node) return null
        return (
          <ButtonLink
            unstyled
            focusStyled
            className="hover:text-dark-bright transition-colors underline"
            href={`/${teamSlug}/${repoSlug}/explore/${branchOrCommit}/${node.parentKey}#${node.value.id}`}
          >
            {node.value.title}
          </ButtonLink>
        )
      }
      case 'layout-block': {
        return trigger.blockIds.map((bId, index) => {
          const node = getNode(bId)
          if (!node) return null

          return (
            <React.Fragment key={bId}>
              <ButtonLink
                unstyled
                focusStyled
                className="hover:text-dark-bright transition-colors underline"
                href={`/${teamSlug}/${repoSlug}/explore/${branchOrCommit}/${node.parentKey}#${node.value.id}`}
              >
                {node.value.title}
              </ButtonLink>
              {index === trigger.blockIds.length - 1 ? '' : ', '}
            </React.Fragment>
          )
        })
      }
      default:
        return null
    }
  }, [trigger, branchOrCommit, getNode, repoSlug, teamSlug])

  const subType = React.useMemo<React.ReactNode>(() => {
    if (!trigger || trigger.type === 'commit') return null
    let description: [string, string] = ['A', 'block']
    switch (trigger.type) {
      case 'layout-block':
        description = ['', '']
        break
      case 'event-block':
        description = ['An', 'event']
        break
      case 'list-block':
        description = ['A', 'row']
        break
    }
    const [article, block] = description
    const eventsToRender = trigger.events

    if (!eventsToRender || eventsToRender.length === 0) return null
    if (eventsToRender.length === 1) {
      switch (eventsToRender[0]) {
        case 'created':
          return 'Has a new ' + block
        case 'updated':
          return `${article} ${block} is updated`
        case 'deleted':
          return `${article} ${block} is deleted`
      }
    }

    return `${article} ${block} is ${eventsToRender
      .slice(0, -1)
      .join(', ')} or ${eventsToRender.slice(-1)}`
  }, [trigger])

  return (
    <span
      className={clsx(
        'flex items-center rounded px-1 gap-x-0.5 [&>span]:rounded [&>span]:px-0.5 [&>span]:py-0.5 min-h-6',
        status === 'both-changed' &&
          isConflictingOnTrigger &&
          '!bg-yellow-diff/20',
        status === 'both-changed' &&
          condition === 'removed' &&
          'bg-red-diff/30',
        status === 'both-changed' && condition === 'added' && 'bg-green-diff/30'
      )}
    >
      <span
        className={clsx(
          status === 'block-changed' &&
            isConflictingOnTrigger &&
            '!bg-yellow-diff/20',
          status === 'block-changed' &&
            condition === 'removed' &&
            'bg-red-diff/30',
          status === 'block-changed' &&
            condition === 'added' &&
            'bg-green-diff/30'
        )}
      >
        {firstType}
      </span>
      {subType && (
        <>
          <ArrowRightIcon />
          <span
            className={clsx(
              status === 'event-changed' &&
                isConflictingOnTrigger &&
                '!bg-yellow-diff/20',
              status === 'event-changed' &&
                condition === 'removed' &&
                'bg-red-diff/30',
              status === 'event-changed' &&
                condition === 'added' &&
                'bg-green-diff/30'
            )}
          >
            {subType}
          </span>
        </>
      )}
    </span>
  )
}

function DoDiff({
  change,
  condition,
}: WorkflowBlockDiffProps): React.ReactNode {
  const isConflictingOnActions =
    change.status === 'conflict' &&
    change.conflictPaths.includes('value.actions')
  const { data: repoMembers } = useRepoMembers()
  const isDeletedAndConditionIsAdded =
    condition === 'added' && change.status === 'deleted'
  const blockToCompare = React.useMemo(() => {
    const test = isDeletedAndConditionIsAdded
      ? ((change.conflicting?.value ?? change.previous) as WorkflowBlock)
      : ((condition === 'removed'
          ? (change.conflicting ?? change.previous)
          : change.current) as WorkflowBlock)

    return test
  }, [change, condition, isDeletedAndConditionIsAdded])

  return Object.entries(blockToCompare?.value.actions ?? {})
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([key, action]) => (
      <ActionDiff
        condition={condition}
        key={key}
        members={repoMembers}
        action={action}
        id={key}
        blockToCompare={blockToCompare}
        isConflictingOnActions={isConflictingOnActions}
      />
    ))
}

function ActionDiff({
  condition,
  id: actionKey,
  members,
  blockToCompare,
  action,
  isConflictingOnActions,
}: {
  condition: 'removed' | 'added'
  id: string
  members: PublicUserData[] | null | undefined
  blockToCompare: WorkflowBlock | null
  action: WorkflowBlock['value']['actions'][string]
  isConflictingOnActions: boolean
}) {
  const getNode = useWorkingTreeStore((s) => s.tree?.getNode)
  const status = React.useMemo(() => {
    if (blockToCompare?.type !== 'workflow') return 'both-changed'
    const actionToCompare = blockToCompare?.value.actions[actionKey]
    if (actionToCompare?.type !== action.type) return 'both-changed'
    if (!isEqual(actionToCompare, action)) return 'value-changed'
    return 'unmodified'
  }, [blockToCompare, action, actionKey])

  const value = React.useMemo(() => {
    switch (action.type) {
      case 'ai-autofill':
      case 'ai-autofill-variant': {
        const title =
          getNode && action.blockId
            ? getNode(action.blockId)?.value.title || 'Untitled'
            : action.blockId
        return title
      }
      case 'webhook':
        return action.url
      case 'notification': {
        const usernames: string[] = []
        action.userIds.forEach((id) => {
          const member = members?.find((m) => m.id === id)
          if (!member) return
          usernames.push(makeShortNameFromClerkUser(member))
        })
        return usernames.join(', ')
      }
    }
  }, [action, getNode, members])

  const byAgent = React.useMemo(() => {
    switch (action.type) {
      case 'ai-autofill':
      case 'ai-autofill-variant': {
        const agent = getNode?.(action.agentId)?.value
        const agentTitle = agent
          ? agent.title || 'Untitled Agent'
          : action.agentId
        return agentTitle
      }
      default:
        return null
    }
  }, [action, getNode])

  return (
    <span
      key={actionKey}
      className={clsx(
        'w-full overflow-hidden max-w-max col-start-2 inline-flex rounded px-1 gap-x-0.5 [&>span]:rounded [&>span]:px-0.5 [&>span]:py-0.5',
        status === 'both-changed' &&
          isConflictingOnActions &&
          '!bg-yellow-diff/20',
        status === 'both-changed' &&
          condition === 'removed' &&
          'bg-red-diff/30',
        status === 'both-changed' && condition === 'added' && 'bg-green-diff/30'
      )}
    >
      <span className="capitalize-first shrink-0">{action.type}</span>
      <ArrowRightIcon className="shrink-0 h-6" />
      <span
        className={clsx(
          'overflow-hidden',
          value === '' && 'italic text-dark-muted',
          action.type === 'webhook' && 'break-words',
          status === 'value-changed' &&
            isConflictingOnActions &&
            '!bg-yellow-diff/20',
          status === 'value-changed' &&
            condition === 'removed' &&
            'bg-red-diff/30',
          status === 'value-changed' &&
            condition === 'added' &&
            'bg-green-diff/30'
        )}
      >
        {value || 'Empty'}
        {byAgent && (
          <>
            <span> by </span>
            <span>{byAgent || 'Empty'}</span>
          </>
        )}
      </span>
    </span>
  )
}
